import { default as React, useState, useContext, useCallback } from "react";
import { firestore } from "../services/firebase";
import { captureError } from "../lib/errorReporting";

const stateRegionsRef = firestore.collection("StateRegionSubregion");

const StateRegionContext = React.createContext([]);
const StateRegionActionsContext = React.createContext({});

const normalizeStateDoc = (doc) => {
	const data = doc?.data ? doc.data() : doc || {};
	return {
		id: doc?.id || data.id || "",
		name: data.name || data.Name || "",
		abbr: data.abbr || data.Abbr || "",
		regions: Array.isArray(data.regions) ? data.regions : []
	};
};

const generateId = () => {
	const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
	let autoId = "";
	for (let i = 0; i < 20; i++) {
		autoId += chars.charAt(Math.floor(Math.random() * chars.length));
	}
	return autoId;
};

export function StateRegionSubregionProvider({ children }) {
	const [states, setStates] = useState([]);

	const fetchStateRegions = useCallback(() => {
		const data = [];
		stateRegionsRef
			.orderBy("name")
			.get()
			.then((snapshot) => {
				snapshot.forEach((doc) => {
					data.push(normalizeStateDoc(doc));
				});
				setStates(data);
			})
			.catch((err) => {
				captureError(err, { action: "fetchStateRegions" });
				console.error("Failed to fetch state regions", err);
			});
	}, []);

	const saveState = useCallback(async (stateId, payload) => {
		const isNew = stateId === "new";
		const docId = isNew ? generateId() : stateId;
		try {
			await stateRegionsRef.doc(docId).set(
				{
					name: payload?.name || "",
					abbr: payload?.abbr || "",
					regions: Array.isArray(payload?.regions) ? payload.regions : []
				},
				{ merge: true }
			);
			return docId;
		} catch (err) {
			captureError(err, { action: "saveState", stateId });
			throw err;
		}
	}, []);

	const updateState = useCallback(async (stateId, updater) => {
		const docRef = stateRegionsRef.doc(stateId);
		try {
			return await firestore.runTransaction(async (tx) => {
				const snap = await tx.get(docRef);
				const current = normalizeStateDoc(snap.exists ? snap : { id: stateId, data: () => ({}) });
				const next = updater(current);
				tx.set(
					docRef,
					{
						name: next.name || "",
						abbr: next.abbr || "",
						regions: Array.isArray(next.regions) ? next.regions : []
					},
					{ merge: true }
				);
				return next;
			});
		} catch (err) {
			captureError(err, { action: "updateState", stateId });
			throw err;
		}
	}, []);

	const deleteState = useCallback(async (stateId) => {
		try {
			await stateRegionsRef.doc(stateId).delete();
		} catch (err) {
			captureError(err, { action: "deleteState", stateId });
			throw err;
		}
	}, []);

	const saveRegion = useCallback(async (stateId, regionId, region) => {
		if (!stateId) {
			throw new Error("State is required");
		}
		return updateState(stateId, (current) => {
			const isNew = regionId === "new";
			const nextId = isNew ? generateId() : regionId;
			const regions = Array.isArray(current.regions) ? [...current.regions] : [];
			const index = regions.findIndex((item) => item.id === nextId);
			const nextRegion = {
				id: nextId,
				name: region?.name || "",
				subregions: Array.isArray(region?.subregions) ? region.subregions : []
			};
			if (index >= 0) {
				regions[index] = { ...regions[index], ...nextRegion };
			} else {
				regions.push(nextRegion);
			}
			return { ...current, regions };
		});
	}, [updateState]);

	const deleteRegion = useCallback(async (stateId, regionId) => {
		if (!stateId || !regionId) {
			return;
		}
		return updateState(stateId, (current) => {
			const regions = (current.regions || []).filter((region) => region.id !== regionId);
			return { ...current, regions };
		});
	}, [updateState]);

	const saveSubregion = useCallback(async (stateId, regionId, subregionId, subregion) => {
		if (!stateId || !regionId) {
			throw new Error("State and region are required");
		}
		return updateState(stateId, (current) => {
			const regions = Array.isArray(current.regions) ? [...current.regions] : [];
			const regionIndex = regions.findIndex((region) => region.id === regionId);
			if (regionIndex === -1) {
				throw new Error("Region not found in state");
			}

			const isNew = subregionId === "new";
			const nextId = isNew ? generateId() : subregionId;
			const targetRegion = regions[regionIndex];
			const subregions = Array.isArray(targetRegion.subregions) ? [...targetRegion.subregions] : [];
			const subIndex = subregions.findIndex((item) => item.id === nextId);
			const nextSubregion = {
				id: nextId,
				name: subregion?.name || ""
			};
			if (subIndex >= 0) {
				subregions[subIndex] = { ...subregions[subIndex], ...nextSubregion };
			} else {
				subregions.push(nextSubregion);
			}

			regions[regionIndex] = { ...targetRegion, subregions };
			return { ...current, regions };
		});
	}, [updateState]);

	const deleteSubregion = useCallback(async (stateId, regionId, subregionId) => {
		if (!stateId || !regionId || !subregionId) {
			return;
		}
		return updateState(stateId, (current) => {
			const regions = Array.isArray(current.regions) ? [...current.regions] : [];
			const regionIndex = regions.findIndex((region) => region.id === regionId);
			if (regionIndex === -1) {
				return current;
			}
			const targetRegion = regions[regionIndex];
			const subregions = (targetRegion.subregions || []).filter((item) => item.id !== subregionId);
			regions[regionIndex] = { ...targetRegion, subregions };
			return { ...current, regions };
		});
	}, [updateState]);

	const refresh = useCallback(() => {
		setStates([]);
		fetchStateRegions();
	}, [fetchStateRegions]);

	return (
		<StateRegionActionsContext.Provider
			value={{
				fetchStateRegions,
				saveState,
				deleteState,
				saveRegion,
				deleteRegion,
				saveSubregion,
				deleteSubregion,
				refresh
			}}
		>
			<StateRegionContext.Provider value={states}>
				{children}
			</StateRegionContext.Provider>
		</StateRegionActionsContext.Provider>
	);
}

export const useStateRegions = () => useContext(StateRegionContext);
export const useStateRegionActions = () => useContext(StateRegionActionsContext);
